import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { 
  getAllExplanationPages, 
  updateExplanationPage, 
  createExplanationPage,
  uploadExplanationVideo,
  deleteExplanationVideo,
  ExplanationPage,
  ExplanationSection 
} from '../services/explanationContentService';

interface PlatformExplanationModeProps {
  isOpen: boolean;
  onClose: () => void;
}

const ADMIN_EMAILS = [
  'info@superkonnected.nl',
  'support@superkonnected.nl',
  'bussinesssecurit@gmail.com'
];

/**
 * PlatformExplanationMode - Tab-based explanation system with inline editing
 */
const PlatformExplanationMode: React.FC<PlatformExplanationModeProps> = ({ isOpen, onClose }) => {
  const [selectedPage, setSelectedPage] = useState<string>('dashboard');
  const [pages, setPages] = useState<ExplanationPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingSection, setEditingSection] = useState<{ pageId: string; sectionIndex: number } | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);

  // Check if user is admin
  useEffect(() => {
    const user = auth.currentUser;
    if (user && ADMIN_EMAILS.includes(user.email?.toLowerCase() || '')) {
      setIsAdmin(true);
    }
  }, []);

  // Load pages from Firebase
  useEffect(() => {
    if (!isOpen) return;

    const loadPages = async () => {
      try {
        setLoading(true);
        const loadedPages = await getAllExplanationPages();
        setPages(loadedPages);
        
        if (loadedPages.length > 0 && !selectedPage) {
          setSelectedPage(loadedPages[0].pageId || loadedPages[0].id);
        }
      } catch (error) {
        console.error('Error loading explanation pages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPages();
  }, [isOpen]);

  // Block body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditMode) {
          setIsEditMode(false);
          setEditingSection(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, isEditMode]);

  const selectedPageContent = pages.find(p => (p.pageId || p.id) === selectedPage);

  const handleSaveSection = async (page: ExplanationPage, sectionIndex: number, updates: Partial<ExplanationSection>) => {
    try {
      const updatedSections = [...page.sections];
      updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], ...updates };
      
      await updateExplanationPage(page.id, {
        sections: updatedSections,
        title: page.title,
        order: page.order,
      });
      
      // Reload pages
      const loadedPages = await getAllExplanationPages();
      setPages(loadedPages);
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Fout bij opslaan');
    }
  };

  const handleUploadVideo = async (page: ExplanationPage, sectionIndex: number, file: File) => {
    if (!file) return;

    setUploading(`${page.id}-${sectionIndex}`);
    try {
      const videoUrl = await uploadExplanationVideo(file, page.id, `section-${sectionIndex}`);
      await handleSaveSection(page, sectionIndex, { videoUrl });
      alert('Video geüpload!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Fout bij uploaden van video');
    } finally {
      setUploading(null);
    }
  };

  const handleAddSection = async (page: ExplanationPage) => {
    try {
      const newSection: ExplanationSection = {
        title: 'Nieuwe sectie',
        videoUrl: '',
        description: '',
        order: page.sections.length,
      };
      
      await updateExplanationPage(page.id, {
        sections: [...page.sections, newSection],
        title: page.title,
        order: page.order,
      });
      
      const loadedPages = await getAllExplanationPages();
      setPages(loadedPages);
      setEditingSection({ pageId: page.id, sectionIndex: page.sections.length });
    } catch (error) {
      console.error('Error adding section:', error);
      alert('Fout bij toevoegen van sectie');
    }
  };

  const handleDeleteSection = async (page: ExplanationPage, sectionIndex: number) => {
    if (!window.confirm('Sectie verwijderen?')) return;

    try {
      const section = page.sections[sectionIndex];
      if (section.videoUrl) {
        await deleteExplanationVideo(section.videoUrl).catch(console.error);
      }
      
      const updatedSections = page.sections.filter((_, i) => i !== sectionIndex);
      await updateExplanationPage(page.id, {
        sections: updatedSections,
        title: page.title,
        order: page.order,
      });
      
      const loadedPages = await getAllExplanationPages();
      setPages(loadedPages);
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Fout bij verwijderen van sectie');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dimmed background */}
      <div 
        className="fixed inset-0 z-[9999] backdrop-blur-sm transition-opacity duration-300"
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
        onClick={onClose}
      />

      {/* Explanation Container */}
      <div 
        className="fixed z-[10000] left-1/2 -translate-x-1/2"
        style={{
          top: 'calc(33% - 100px)',
          width: '90%',
          maxWidth: '900px',
          maxHeight: 'calc(67vh)',
          overflowY: 'auto',
          pointerEvents: isOpen ? 'auto' : 'none',
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Close Button */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            {isAdmin && (
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className="w-8 h-8 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-colors"
                title={isEditMode ? 'Bewerkmodus uit' : 'Bewerkmodus aan'}
              >
                ✏️
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Sluiten"
            >
              <span className="text-2xl font-bold leading-none">×</span>
            </button>
          </div>

          {/* Header */}
          <div className="p-6 md:p-8 pb-4 border-b border-gray-200">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Zo werkt SuperKonnected
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              {isEditMode ? 'Bewerkmodus actief - klik op tekst om te bewerken' : 'Kies een pagina om de uitleg te bekijken'}
            </p>
          </div>

          {/* Page Tabs */}
          <div className="px-6 md:px-8 pt-4 border-b border-gray-200 bg-gray-50">
            <nav className="flex items-center gap-1 overflow-x-auto">
              {pages.map((page) => {
                const pageIdentifier = page.pageId || page.id;
                return (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPage(pageIdentifier)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                      selectedPage === pageIdentifier
                        ? 'text-gray-900 bg-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    {page.title}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 max-h-[calc(67vh-200px)] overflow-y-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Laden...</p>
              </div>
            ) : !selectedPageContent ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Geen content beschikbaar voor deze pagina.</p>
              </div>
            ) : (
              <>
                {/* Page Title */}
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-6">
                  {selectedPageContent.title}
                </h3>

                {/* Video Sections */}
                <div className="space-y-8">
                  {selectedPageContent.sections
                    .sort((a, b) => a.order - b.order)
                    .map((section, index) => {
                      const isEditing = editingSection?.pageId === selectedPageContent.id && editingSection?.sectionIndex === index;
                      const isUploadingVideo = uploading === `${selectedPageContent.id}-${index}`;
                      
                      return (
                        <div key={section.id || index} className="space-y-3 border border-gray-200 rounded-lg p-4">
                          {/* Section Header */}
                          <div className="flex justify-between items-start">
                            {isEditMode && isEditing ? (
                              <input
                                type="text"
                                defaultValue={section.title}
                                onBlur={(e) => {
                                  if (e.target.value !== section.title) {
                                    handleSaveSection(selectedPageContent, index, { title: e.target.value });
                                  }
                                  setEditingSection(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                  }
                                }}
                                className="text-lg md:text-xl font-medium text-gray-800 border border-orange-500 rounded px-2 py-1 flex-1"
                                autoFocus
                              />
                            ) : (
                              <h4 
                                className="text-lg md:text-xl font-medium text-gray-800 cursor-pointer hover:text-orange-600 transition-colors"
                                onClick={() => {
                                  if (isEditMode) {
                                    setEditingSection({ pageId: selectedPageContent.id, sectionIndex: index });
                                  }
                                }}
                              >
                                {section.title}
                              </h4>
                            )}
                            
                            {isEditMode && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingSection({ pageId: selectedPageContent.id, sectionIndex: index })}
                                  className="text-orange-600 hover:text-orange-800 text-sm"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeleteSection(selectedPageContent, index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Video */}
                          <div className="relative w-full rounded-lg overflow-hidden bg-gray-100" style={{ aspectRatio: '16/9' }}>
                            {section.videoUrl ? (
                              <video
                                src={section.videoUrl}
                                controls
                                className="w-full h-full object-contain"
                                preload="metadata"
                              >
                                Je browser ondersteunt geen video element.
                              </video>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                Geen video
                              </div>
                            )}
                            
                            {isEditMode && (
                              <div className="absolute top-2 right-2">
                                <label className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm cursor-pointer">
                                  {isUploadingVideo ? 'Uploaden...' : section.videoUrl ? 'Vervang video' : 'Upload video'}
                                  <input
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleUploadVideo(selectedPageContent, index, file);
                                      }
                                    }}
                                    disabled={isUploadingVideo}
                                    className="hidden"
                                  />
                                </label>
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          {isEditMode && isEditing ? (
                            <textarea
                              defaultValue={section.description || ''}
                              onBlur={(e) => {
                                if (e.target.value !== (section.description || '')) {
                                  handleSaveSection(selectedPageContent, index, { description: e.target.value });
                                }
                                setEditingSection(null);
                              }}
                              placeholder="Beschrijving (optioneel)"
                              rows={2}
                              className="w-full px-3 py-2 border border-orange-500 rounded-lg text-sm text-gray-600"
                              autoFocus
                            />
                          ) : (
                            section.description && (
                              <p 
                                className="text-gray-600 text-sm md:text-base cursor-pointer hover:text-orange-600 transition-colors"
                                onClick={() => {
                                  if (isEditMode) {
                                    setEditingSection({ pageId: selectedPageContent.id, sectionIndex: index });
                                  }
                                }}
                              >
                                {section.description}
                              </p>
                            )
                          )}
                        </div>
                      );
                    })}
                  
                  {/* Add Section Button */}
                  {isEditMode && (
                    <button
                      onClick={() => handleAddSection(selectedPageContent)}
                      className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
                    >
                      + Sectie toevoegen
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 md:p-8 pt-4 border-t border-gray-200 bg-gray-50 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Klaar met lezen
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlatformExplanationMode;
